import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useListTeams from '../Admin/Hooks/useListTeams'
import { useListAllTeamMembers } from '../Admin/Hooks/useListTeamMembers'

const normalize = (v) => `${v ?? ''}`.trim().toLowerCase()

// Roles whose reach covers the whole building, so team rosters skip the
// team's visibility scoping for them. Tenant Admins are deliberately absent:
// their admin Teams page is scoped to their tenant, so in the occupant module
// they see what the team's visibility grants any occupant on their floor / in
// their tenant.
export const BUILDING_WIDE_VIEWER_ROLES = ['SUPER_ADMIN', 'PROPERTY_ADMIN', 'BUILDING_ADMIN', 'FIRE_SAFETY_DIRECTOR']

export const isBuildingWideViewer = (user) =>
    (user?.roles || []).some((role) => BUILDING_WIDE_VIEWER_ROLES.includes(role))

// The members of a team the viewer may see. Building-wide roles see the full
// roster; everyone else — team members and Tenant Admins included — sees what
// the team's visibility grants: ALL_FLOOR → members sharing their floor,
// ALL_TENANT → members sharing their tenant, ALL_BUILDING / MEMBERS_ONLY →
// everyone on the roster. The viewer's own record is always kept so a member
// with no floor on file still finds themselves. `floor` overrides the
// occupancy floor (callers fall back to the member record's floor when the
// occupancy d1 has no floor segment). Shared by the team page roster and the
// landing-grid member counts so the two always agree.
export const scopeMembersByVisibility = ({ team, members, user, tenantName, floor }) => {
    const all = members || []
    if (isBuildingWideViewer(user)) return all
    const keepOwn = (m) => isOwnMembership(m, user)
    switch (team?.visibility) {
        case 'ALL_FLOOR': {
            const myFloor = normalize(floor ?? user?.floor)
            return all.filter((m) => keepOwn(m) || (Boolean(myFloor) && normalize(m.floor) === myFloor))
        }
        case 'ALL_TENANT': {
            const myTenant = normalize(tenantName)
            return all.filter((m) => keepOwn(m) || (Boolean(myTenant) && normalize(m.tenant) === myTenant))
        }
        default:
            return all
    }
}

// A team-member record belongs to the current user when its sk suffix matches
// the occupant's pk or d2 (AssignTeamMember writes TEAM_MEMBER#<pk>, but
// legacy/seeded records may carry the d2 — same fallback ContactOverlay uses),
// or, failing both, when the emails match.
export const isOwnMembership = (member, user) => {
    const suffix = (member?.sk || '').split('#')[1]
    if (suffix && (suffix === user?.pk || (user?.d2 && suffix === user.d2))) return true
    const email = normalize(member?.email)
    return Boolean(email) && email === normalize(user?.email)
}

// The teams an occupant can see: every team they are a member of, plus teams
// whose visibility grants them view access — ALL_BUILDING to everyone in the
// building, ALL_TENANT/ALL_FLOOR when the user shares a tenant/floor with one
// of the team's members. MEMBERS_ONLY teams stay members-only. Archived teams
// are never shown.
function useMyTeams() {
    const user = useSelector((state) => state.app.user)
    const building = useSelector((state) => state.app.building)
    const tenantName = useSelector((state) => state.app.tenant?.name)

    const teamsD1 = building?.d1 && building?.pk ? `${building.d1}#${building.pk}` : null
    const { teams, loading: teamsLoading, error } = useListTeams(teamsD1)

    const activeTeams = useMemo(() => (teams || []).filter((t) => t.active !== false), [teams])
    const { allTeamMembers, loading: membersLoading } = useListAllTeamMembers(activeTeams)

    // team pk → this user's membership record on that team
    const membershipByTeam = useMemo(() => {
        const map = {}
        allTeamMembers.forEach((m) => {
            if (isOwnMembership(m, user)) map[m.pk] = m
        })
        return map
    }, [allTeamMembers, user])

    const visibleTeams = useMemo(() => {
        const membersByTeam = {}
        allTeamMembers.forEach((m) => {
            if (!membersByTeam[m.pk]) membersByTeam[m.pk] = []
            membersByTeam[m.pk].push(m)
        })
        const userTenant = normalize(tenantName)
        const userFloor = normalize(user?.floor)

        return activeTeams.filter((team) => {
            if (membershipByTeam[team.pk]) return true
            const members = membersByTeam[team.pk] || []
            switch (team.visibility) {
                case 'ALL_BUILDING':
                    return true
                case 'ALL_TENANT':
                    return Boolean(userTenant) && members.some((m) => normalize(m.tenant) === userTenant)
                case 'ALL_FLOOR':
                    return Boolean(userFloor) && members.some((m) => normalize(m.floor) === userFloor)
                default:
                    return false
            }
        })
    }, [activeTeams, allTeamMembers, membershipByTeam, tenantName, user])

    // Live member counts off the rosters, so the landing cards agree with the
    // team page instead of trusting the stored nrOfMembers counter. Counted
    // through the same visibility scoping as the team page roster, so a card
    // never advertises members the viewer can't see once they open the team.
    const memberCounts = useMemo(() => {
        const membersByTeam = {}
        allTeamMembers.forEach((m) => {
            if (!membersByTeam[m.pk]) membersByTeam[m.pk] = []
            membersByTeam[m.pk].push(m)
        })
        const counts = {}
        activeTeams.forEach((team) => {
            counts[team.pk] = scopeMembersByVisibility({
                team,
                members: membersByTeam[team.pk],
                user,
                tenantName,
                floor: user?.floor || membershipByTeam[team.pk]?.floor || null,
            }).length
        })
        return counts
    }, [allTeamMembers, activeTeams, membershipByTeam, user, tenantName])

    return {
        loading: teamsLoading || membersLoading,
        error,
        visibleTeams,
        membershipByTeam,
        memberCounts,
        membersLoaded: !membersLoading && allTeamMembers.length > 0,
    }
}

export default useMyTeams
